/* ──────────────────────────────────────────────────────────────
   src/components/AccountMenu.tsx
   Menú de usuario: documentos · reuniones · vacaciones/ausencias · logout
   ────────────────────────────────────────────────────────────── */
import { useState, useEffect } from 'react';
import { IconButton, Avatar, Menu, MenuItem, ListItemIcon, ListItemText, Badge } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DescriptionIcon from '@mui/icons-material/Description'; // 📄 documentos
import BeachAccessIcon from '@mui/icons-material/BeachAccess'; // 🏖 vacaciones
import EventIcon from '@mui/icons-material/Event'; // ⭐ reuniones
import AssignmentIcon from '@mui/icons-material/Assignment'; // 📋 formularios/evaluaciones
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { logout as apiLogout } from '../api/logout';
import { getFormularioPendiente, getEvaluacionesPendientes, getFormulario } from '../api/formularios';

export default function AccountMenu() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation<'translation'>();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hasPendientes, setHasPendientes] = useState(false);

  /* ─── sin usuario ~ no mostrar nada ─── */
  if (!user) return null;

  const initials = user.email?.[0]?.toUpperCase() ?? '?';
  const roles: string[] = Array.isArray(user.roles) ? user.roles : [user.roles];

  /* ROLES QUE NO SON STAFF */
  const isProfesor = roles.includes('profesor');
  const isCliente = roles.includes('cliente');
  const isConsultor = roles.includes('consultor');

  /* visibles para TODOS salvo profesor, consultor y cliente */
  const showDocs = !isProfesor && !isCliente;
  const showVac = showDocs;

  /* ⭐⭐ NUEVO → Mis reuniones solo visible si es STAFF ⭐⭐ */
  const isStaff = !isProfesor && !isCliente && !isConsultor;
  const showMisReuniones = isStaff;

  /* ─── Cargar pendientes ─── */
  useEffect(() => {
    if (!showDocs || !user?.id) {
      setHasPendientes(false);
      return;
    }

    const checkPendientes = async () => {
      try {
        let tienePendientes = false;

        // Verificar formulario pendiente
        const formularioPendiente = await getFormularioPendiente();
        if (formularioPendiente) {
          // Verificar que no sea una evaluación
          const formularioCompleto = await getFormulario(formularioPendiente.id);
          if (!formularioCompleto.esEvaluacion) {
            tienePendientes = true;
          }
        }

        // Si no hay formulario pendiente, verificar evaluaciones pendientes
        if (!tienePendientes) {
          const evaluacionesPendientes = await getEvaluacionesPendientes();
          const evaluacionesUsuario = evaluacionesPendientes.filter(
            (e: any) => e.usuarioId === user.id
          );
          tienePendientes = evaluacionesUsuario.length > 0;
        }

        setHasPendientes(tienePendientes);
      } catch (error) {
        console.error('Error al verificar pendientes:', error);
        setHasPendientes(false);
      }
    };

    checkPendientes();
    // Refrescar cada 30 segundos
    const interval = setInterval(checkPendientes, 30000);
    return () => clearInterval(interval);
  }, [showDocs, user?.id]);

  /* ─── helpers ─── */
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  /** Cerrar sesión: revoca token, borra cookie y limpia estado */
  const handleLogout = async () => {
    await apiLogout();
    setUser(null);
    navigate('/login');
  };

  return (
    <>
      <IconButton aria-label={t('accountMenu.openMenu') as string} onClick={handleOpen}>
        <Badge
          overlap="circular"
          variant="dot"
          invisible={!hasPendientes}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiBadge-dot': {
              backgroundColor: '#ff4d4f',
              width: 8,
              height: 8,
              border: '2px solid #fff',
            },
          }}
        >
          <Avatar sx={{ width: 32, height: 32 }}>{initials}</Avatar>
        </Badge>
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {/* ─── Mis documentos (solo STAFF + consultores + profesores?) ─── */}
        {showDocs && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/mis-documentos');
            }}
          >
            <ListItemIcon>
              <DescriptionIcon fontSize="small" sx={{ color: '#000' }} />
            </ListItemIcon>
            <ListItemText>{t('menu.myDocuments', 'Mis documentos')}</ListItemText>
          </MenuItem>
        )}

        {/* ⭐⭐⭐ MIS REUNIONES — SOLO STAFF — NUEVO ⭐⭐⭐ */}
        {showMisReuniones && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/mis-reuniones');
            }}
          >
            <ListItemIcon>
              <EventIcon fontSize="small" sx={{ color: '#000' }} />
            </ListItemIcon>
            <ListItemText>{t('menu.myMeetings', 'Mis reuniones')}</ListItemText>
          </MenuItem>
        )}

        {/* ─── Mis Formularios/Evaluaciones ─── */}
        {showDocs && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/mis-formularios');
            }}
          >
            <ListItemIcon>
              <Badge
                variant="dot"
                invisible={!hasPendientes}
                sx={{
                  '& .MuiBadge-dot': {
                    backgroundColor: '#ff4d4f',
                    width: 8,
                    height: 8,
                  },
                }}
              >
                <AssignmentIcon fontSize="small" sx={{ color: '#000' }} />
              </Badge>
            </ListItemIcon>
            <ListItemText>{t('menu.myFormsEvaluations', 'Mis Formularios/Evaluaciones')}</ListItemText>
          </MenuItem>
        )}

        {/* ─── Vacaciones / Ausencias ─── */}
        {showVac && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/vacaciones');
            }}
          >
            <ListItemIcon>
              <BeachAccessIcon fontSize="small" sx={{ color: '#000' }} />
            </ListItemIcon>
            <ListItemText>{t('menu.vacations', 'Vacaciones/Ausencias')}</ListItemText>
          </MenuItem>
        )}

        {/* ─── Cerrar sesión ─── */}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: '#000' }} />
          </ListItemIcon>
          <ListItemText>{t('common.logout', 'Cerrar sesión')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
